import { DateTime } from 'luxon';
import {
  AddressInfo,
  ContactDetails,
  LadingInfo,
} from '../../work-orders/work-order-details/work-order.interfaces';
import { PanelContact, PanelContactRole, PanelDetails, PanelFact } from './work-order-panel.models';

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

export function contactAddresses(
  contact: ContactDetails | undefined,
  selectedId?: string | null,
): AddressInfo[] {
  const addresses = contact?.address || [];
  // A specified stop address must match exactly. Never substitute another branch
  // of the same company if the selected address wasn't returned by the API.
  return selectedId ? addresses.filter((address) => address.contact_address_uuid === selectedId) : addresses;
}

export function addressLines(address: AddressInfo): string[] {
  return [
    text(address.address_street),
    text(address.address_extended),
    [text(address.address_locality), text(address.address_region)].filter(Boolean).join(', '),
    [text(address.address_postal_code), text(address.address_country)].filter(Boolean).join(' · '),
  ].filter(Boolean);
}

export function panelContact(details: PanelDetails, role: PanelContactRole): PanelContact {
  const contact = details[role];
  const lading = details.lading;
  const info =
    contact?.contact?.find((item) => item.contact_uuid === lading[`${role}_contact_uuid`]) ||
    contact?.contact?.[0];
  const selectedId =
    role === 'shipper'
      ? lading.shipper_contact_address_uuid
      : role === 'consignee'
        ? lading.consignee_contact_address_uuid
        : null;
  const addresses = contactAddresses(contact, selectedId);
  const person = [info?.contact_name_given, info?.contact_name_family].map(text).filter(Boolean).join(' ');
  const organization = text(lading[`${role}_name`]) || text(info?.contact_organization);
  const phones = (contact?.subcontacts || []).map((phone) => ({
    name: [phone.contact_name_given, phone.contact_name_family].map(text).filter(Boolean).join(' '),
    description: text(phone.phone_description),
    number: text(phone.phone_number),
  }));
  for (const phone of contact?.phone || []) {
    if (!phones.some((existing) => existing.number === text(phone.phone_number))) {
      phones.push({ name: '', description: text(phone.phone_description), number: text(phone.phone_number) });
    }
  }

  const titles = { shipper: 'Shipper', consignee: 'Consignee', customer: 'Customer', carrier: 'Carrier' };
  const subtitles = { shipper: 'Pickup', consignee: 'Delivery', customer: 'Customer', carrier: 'Carrier' };
  return {
    role,
    title: titles[role],
    subtitle: subtitles[role],
    name: organization || person || `${titles[role]} not assigned`,
    person: person && person !== organization ? person : '',
    email: text(info?.contact_email),
    addresses: addresses.map((address) => ({
      lines: addressLines(address),
      timezone: text(address.timezone_id),
      type: text(address.address_type),
    })),
    addressMessage: selectedId ? 'Selected address unavailable.' : 'No address available.',
    phones: phones
      .filter((phone) => phone.number)
      .map((phone) => ({
        ...phone,
        href: `tel:${phone.number.replace(/[^+0-9,*#;]/g, '')}`,
      })),
    reference:
      role === 'shipper'
        ? text(lading.pickup_reference_number)
        : role === 'consignee'
          ? text(lading.delivery_reference_number)
          : '',
    hours:
      role === 'shipper'
        ? text(lading.load_start_hours)
        : role === 'consignee'
          ? text(lading.load_end_hours)
          : '',
  };
}

function quantity(value: unknown, unit?: string | null): string {
  const amount = text(value);
  return amount ? [amount, text(unit)].filter(Boolean).join(' ') : 'Not set';
}

export function freightFacts(lading: LadingInfo): PanelFact[] {
  return [
    { label: 'Pieces', value: quantity(lading.load_pieces) },
    { label: 'Pallets', value: quantity(lading.load_pallets) },
    { label: 'Weight', value: quantity(lading.load_weight, lading.load_weight_unit) },
    { label: 'Actual weight', value: quantity(lading.load_weight_actual, lading.load_weight_unit) },
    { label: 'Length', value: quantity(lading.load_length, lading.load_length_unit) },
    { label: 'Load type', value: text(lading.partial_or_full) || 'Not set' },
  ];
}

function stopTime(value: string | null | undefined, timezone: string | null | undefined): string {
  if (!value) return 'Not set';
  const parsed = DateTime.fromSQL(value.replace('T', ' ').slice(0, 19), { zone: 'America/Toronto' });
  const local = timezone ? parsed.setZone(timezone) : parsed;
  if (!local.isValid) return value;
  return `${local.toFormat('MMM d, yyyy · h:mm a')} · ${timezone || 'America/Toronto'}`;
}

export function scheduleFacts(details: PanelDetails): PanelFact[] {
  const lading = details.lading;
  const shipperZone = contactAddresses(details.shipper, lading.shipper_contact_address_uuid)[0]?.timezone_id;
  const consigneeZone = contactAddresses(details.consignee, lading.consignee_contact_address_uuid)[0]
    ?.timezone_id;
  return [
    { label: 'Pickup', value: stopTime(lading.load_start, shipperZone) },
    { label: 'Delivery', value: stopTime(lading.load_end, consigneeZone) },
    { label: 'Estimated pickup', value: text(lading.estimated_load_start)?.slice(0, 10) || 'Not set' },
    { label: 'Estimated delivery', value: text(lading.estimated_load_end)?.slice(0, 10) || 'Not set' },
  ];
}
